import copy from 'fast-clone';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import webApi from '../../../Lib/WebApi';
import { callApi, camelizeObjectKeys } from '../../Helpers/rxjsHelpers';

// Define specifics around which data should be retrieved from where for different equipment types
// * endpoint: default get/post/patch endpoint.
const equipmentTypeEndpointConfiguration = {
  Sieve: { endpoint: 'equipment.sieves' },
  'Sieve Set': { endpoint: 'equipment.sievesets' },
  'Grooving Tool': { endpoint: 'equipment.groovingtools' },
  'Liquid Limit Cup': { endpoint: 'equipment.liquidlimitapparatus' },
  Hammer: { endpoint: 'equipment.hammers' },
  Balance: { endpoint: 'equipment.balances' },
  BitumenCentrifugeExtractor: { endpoint: 'equipment.bitumencentrifugeextractors' },
  Centrifuge: { endpoint: 'equipment.centrifuges' },
  Oven: { endpoint: 'equipment.ovens' },
  'Compression Machine': { endpoint: 'equipment.compressionmachines' },
  'Load Cell': { endpoint: 'equipment.loadcells' },
  'Deflection Gauge': { endpoint: 'equipment.deflectiongauges' },
  'Moisture Tin': { endpoint: 'equipment.moisturetins' },
  'Nuclear Density Gauge': { endpoint: 'equipment.nucleargauge' },
};

const retrieveEndpointConfigOrThrow = (equipmentType, path) => {
  let config = copy(equipmentTypeEndpointConfiguration[equipmentType]);
  if (!config) {
    throw new Error(`The equipment type "${equipmentType}" is not supported by this client.`);
  }
  config.endpoint = path ? `${config.endpoint}.${path}` : config.endpoint;
  if (!webApi[config.endpoint]) {
    throw new Error(`The equipment type "${equipmentType}" does not have an endpoint '${config.endpoint}'.`);
  }
  return config;
};

const mapRootPathMapResponse = (r) => camelizeObjectKeys(r);

export const callApiPutEquipment = (equipmentType, body, params, path) => {
  let { endpoint } = retrieveEndpointConfigOrThrow(equipmentType, path);
  return callApi(webApi[endpoint].put(body, params)).pipe(map((r) => (path ? r : mapRootPathMapResponse(r.Body))));
};

export const callApiPostEquipment = (equipmentType, body, params, path) => {
  let { endpoint } = retrieveEndpointConfigOrThrow(equipmentType, path);
  return callApi(webApi[endpoint].post(body, params)).pipe(map((r) => (path ? r : mapRootPathMapResponse(r.Body))));
};

/**
 * Returns type-specific equipment data model.
 *
 * NOTE: Currently we cannot request equipment from type-specific endpoints with calibration status for a provided date.
 * Status will always be obtained for the current date (for the equipment's laboratory)
 */
export const callApiGetEquipment = (equipmentType, equipmentId, includeHeader = true, path = null) => {
  let { endpoint } = retrieveEndpointConfigOrThrow(equipmentType, path);

  const gets = [];

  // should include everything required to render the equipment so map all props, also make sure they become camel cased.
  gets.push({ target: endpoint, mapResponse: !path && mapRootPathMapResponse });

  return forkJoin(
    // Make individual gets
    ...gets.map(({ target }) => callApi(webApi[target].get({ equipmentId }))),
    // Combine result Bodies into a single object.
    (...responses) =>
      responses.reduce(
        (previous, current, index) =>
          gets[index].mapResponse ? { ...previous, ...gets[index].mapResponse(current.Body) } : previous,
        {}
      )
  );
};

/**
 * Returns generic equipment status model, where calibration status is obtained for the provided date
 */
export const callApiGetStatus = (equipmentId, atDate) => {
  return callApi(webApi.equipment.status.get({ equipmentId, atDate })).pipe(map((response) => response.Body));
};

/**
 * Returns generic equipment data model, where calibration status is obtained for the provided date
 */
export const callApiGetEquipmentData = (equipmentId, atDate) => {
  return callApi(webApi.equipment.data.get({ equipmentId, atDate })).pipe(map((response) => response.Body));
};
